import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Chip, IconButton, Paper, SvgIcon, TableSortLabel, Tooltip, Typography, useTheme } from '@mui/material';
import { Trash01 } from '../../../../assets';
import useLogError from '../../../../hooks/useLogError';
import CustomPagination from '../../../../components/forms/Pagination';
import { ModulePermission, Persona, PutPersonaRequest } from '../../../../app/services/api/generated';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import PillList from '../../../../components/shared/PillList';
import CreateUpdateRoleModal from './CreateUpdateRoleModal';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import { ConfirmationDialog } from '../../../../components/shared/dialogs/ConfirmationDialog';
import { UserContext } from '../../../../components/shared/useUser';

interface RolesTableProps {
    personas: Persona[];
    loadData: () => void;
    modules: ModulePermission[];
}

const RolesTable = ({ personas, loadData, modules }: RolesTableProps) => {

  const { t } = useTranslation('manageRoles');
  const theme = useTheme();
  const { deletePersona, editPersona } = useNswagClient();
  const { hasPermissionTo, user, franchises: userFranchises } = React.useContext(UserContext);
  const userTenants = user?.tenants ?? [];
  const [data, setData] = React.useState(personas);
  const [selectedPersona, setSelectedPersona] = React.useState<Persona | undefined>(undefined);
  const [canPersonaBeDeleted, setCanPersonaBeDeleted] = React.useState(true);
  const [showRoleEditModal, setShowRoleEditModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [showDeleteSuccessMessage, setshowDeleteSuccessMessage] = React.useState(false);
  const [showUpdateSuccessMessage, setShowUpdateSuccessMessage] = React.useState(false);
  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
  const orderBy = 'description' as keyof Persona;
  const { logError } = useLogError();
  const [page, setPage] = React.useState(0);

  const rowsPerPage = 10;
  const maxPages = data ? Math.max(1, Math.ceil(data.length / rowsPerPage)) : 1;

  const visibleRows = () => {
    return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  const getLevelInfo = (persona: Persona) => {

    const hasMultiTenant = persona.tenantIds && persona.tenantIds.length > 0;
    if (hasMultiTenant) {
      const tenantNames = (persona.tenantIds ?? [])
        .map(id => userTenants.find(t => t.id === id)?.name ?? `Tenant ${id}`)
        .filter(Boolean);
      return {
        level: 'tenant' as const,
        label: t('tenant'),
        scope: tenantNames.length > 0 ? tenantNames.join(', ') : '-',
        scopeCount: tenantNames.length,
      };
    }

    const hasMultiFranchise = persona.franchiseIds && persona.franchiseIds.length > 0;
    if (hasMultiFranchise) {
      const franchiseNames = (persona.franchiseIds ?? [])
        .map(id => userFranchises.find(f => f.franchiseId === id)?.franchiseName ?? `Franchise ${id}`)
        .filter(Boolean);
      return {
        level: 'franchise' as const,
        label: t('franchise'),
        scope: franchiseNames.length > 0 ? franchiseNames.join(', ') : '-',
        scopeCount: franchiseNames.length,
      };
    }

    const isTenantLevel = persona.tenantId && !persona.franchiseName;
    if (isTenantLevel) {
      const tenant = userTenants.find(t => t.id === persona.tenantId);
      return {
        level: 'tenant' as const,
        label: t('tenant'),
        scope: tenant?.name ?? `Tenant ${persona.tenantId}`,
        scopeCount: 1,
      };
    }

    return {
      level: 'franchise' as const,
      label: t('franchise'),
      scope: persona.franchiseName ?? '-',
      scopeCount: 1,
    };
  };

  const getLevelColors = (level: 'tenant' | 'franchise') => {
    if (level === 'tenant') {
      return {
        background: theme.palette.custom.violet[50],
        border: theme.palette.custom.violet[200],
        text: theme.palette.custom.violet[700],
      };
    }
    return {
      background: theme.palette.success[50],
      border: theme.palette.success[200],
      text: theme.palette.success[700],
    };
  };

  React.useEffect(() => {
    setData(personas);
  }, [personas]);

  const handleSort = (property: keyof Persona) => {
    const isAsc = orderBy === property && order === 'asc';
    if (data) {
      setOrder(isAsc ? 'desc' : 'asc');
      setData(sortedData);
    }
  };

  const sortedData = () => {
    const comparator = (a: Persona, b: Persona): number => {
      const aValue = a.description ?? '';
      const bValue = b.description ?? '';
      return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    };

    return [...personas].sort(comparator);
  };

  const editRole = (persona: Persona | undefined) => {
    setSelectedPersona(persona);
    setCanPersonaBeDeleted(canBeDeleted(persona));
    setShowRoleEditModal(true);
  };

  const handleCloseModal = () => {
    setShowRoleEditModal(false);
    setShowDeleteModal(false);
    setSelectedPersona(undefined);
  };

  const deleteRole = (persona: Persona) => {
    setSelectedPersona(persona);
    setCanPersonaBeDeleted(canBeDeleted(persona));
    setShowDeleteModal(true);
  };

  const handleEditRole = (body: PutPersonaRequest) => {
    editPersona(selectedPersona?.id ?? 0, body)
      .then(() => {
        setShowUpdateSuccessMessage(true);
        setTimeout(() => loadData(), 2000);
      })
      .catch((error) => logError(error));
  };

  const handleDeleteRole = () => {
    deletePersona(selectedPersona?.id ?? 0)
      .then(() => {
        setshowDeleteSuccessMessage(true);
        setTimeout(() => loadData(), 2000);
      })
      .catch((error) => logError(error));

    setShowDeleteModal(false);
    setSelectedPersona(undefined);
  };

  const closeSuccessfulDeleteMessage = () => {
    setshowDeleteSuccessMessage(false);
  };

  const canBeDeleted = (persona?: Persona): boolean => {
    return persona?.users ? persona.users.length === 0 : true;
  };

  return (
    <>
      <TableContainer sx={{ borderRadius: '12px' }}
        component={Paper}>
        <Table
          size="small"
          aria-label="a table"
        >
          <TableHead sx={{ paddingTop: '10px', backgroundColor: theme.palette.custom.gray[200] }}>
            <TableRow>
              <TableCell
                align="left"
                width={'25%'}
                padding='checkbox'
              >
                <TableSortLabel
                  active={true}
                  direction={order}
                  onClick={() => handleSort('description')}>
                  {t('name')}
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="left"
                width={'12%'}
              >
                {t('level')}
              </TableCell>
              <TableCell
                align="left"
                width={'18%'}
              >
                {t('scope')}
              </TableCell>
              <TableCell
                align="left"
                width={'35%'}
              >
                {t('modules')}
              </TableCell>
              <TableCell width={'10%'} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows().map((row) => {
              const levelInfo = getLevelInfo(row);
              const colors = getLevelColors(levelInfo.level);

              return (
                <TableRow
                  key={row.id}
                  sx={hasPermissionTo(['RoleManagementUpdate']) ? { height: '60px', cursor: 'pointer' } : { height: '60px' }}
                  onClick={hasPermissionTo(['RoleManagementUpdate']) ? () => editRole(row) : undefined}
                >
                  <TableCell padding='checkbox'>
                    <Typography variant='textXS'
                      fontWeight={600}
                      color={theme.palette.primary[500]}
                      sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {row.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={levelInfo.label}
                      size="small"
                      sx={{
                        backgroundColor: colors.background,
                        border: `1px solid ${colors.border}`,
                        color: colors.text,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        height: '24px',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {levelInfo.scopeCount > 2 ? (
                      <Tooltip title={levelInfo.scope}
                        arrow>
                        <Typography variant='textXS'
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '150px',
                            display: 'block',
                            cursor: 'pointer',
                          }}>
                          {levelInfo.scopeCount} {levelInfo.level === 'tenant' ? t('tenants') : t('franchises')}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Tooltip title={levelInfo.scope.length > 25 ? levelInfo.scope : ''}
                        arrow>
                        <Typography variant='textXS'
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '150px',
                            display: 'block',
                          }}>
                          {levelInfo.scope}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <PillList labels={row.modules ?? []}></PillList>
                  </TableCell>
                  <TableCell>
                    <IconButton aria-label="delete"
                      disabled={!hasPermissionTo(['RoleManagementDelete'])}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteRole(row);
                      }}>
                      <SvgIcon fontSize='small'><Trash01 color={theme.palette.error[500]} /></SvgIcon>
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomPagination
        page={page}
        setPage={setPage}
        maxPages={maxPages}
        breakpointForChangeDisplay={120000}
      />
      <CreateUpdateRoleModal
        openModal={showRoleEditModal}
        onCancel={handleCloseModal}
        persona={selectedPersona}
        onConfirm={(requestBody: PutPersonaRequest) => handleEditRole(requestBody)}
        modules={modules}
      />

      <ConfirmationDialog open={showDeleteModal}
        message={canPersonaBeDeleted ? t('deleteRoleConfirm') : t('roleCannotBeDeleted')}
        onConfirm={handleDeleteRole}
        onCancel={handleCloseModal}
        icon={<Trash01 />}
        title={canPersonaBeDeleted ? t('deleteRole') : t('roleCannotBeDeletedTitle')}
        variant='bad'
        confirmButtonText='Delete'
        cancelButtonText='Cancel'
        isDisabled={!canPersonaBeDeleted}
      />

      <MessagesSnackbar
        open={showDeleteSuccessMessage}
        onClose={closeSuccessfulDeleteMessage}
        message='Role deleted'
        severity="success"
        duration={null}
      />

      <MessagesSnackbar
        open={showUpdateSuccessMessage}
        onClose={() => setShowUpdateSuccessMessage(false)}
        message={'Role updated successfully'}
        severity="success"
        duration={null}
      />
    </>
  );
};

export default RolesTable;
